import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ClaimFile } from '../../../api/files/interface';
import { actions as filesSliceActions } from '../../../data/files/logic';
import type { StoreState } from '../../../data/store';

export interface FilesHookState {
  files: Record<string, ClaimFile>;
  filesContent?: {
    [fileId: string]: {
      [pageNumber: string]: {
        url: string;
        requestedDate: number;
      };
    };
  };
  filesOCR?: {
    [fileId: string]: {
      [dataVersion: string]: {
        [pageNumber: string]: string;
      };
    };
  };
  filesNotepad?: {
    [fileId: string]: {
      [dataVersion: string]: { [notepadKey: string]: string };
    };
  };
  fileSource: {
    [fileId: string]: string;
  };
  audioTranscripts: {
    [fileId: string]: string;
  };
  actions: StoreState['files']['actions'];
}

interface FilesHookHandler {
  fetchFiles(): void;
  fetchFileContents(data: {
    fileId: string;
    startPage: number;
    endPage: number;
  }): void;
  downloadFile(fileId: string): void;
  deleteFiles(fileIds: string[]): void;
  fetchFilesOCR(data: { fileId: string; dataVersion: string }): void;
  fetchFileNotepads(data: { fileId: string; dataVersion: string }): void;
  fetchFileSource(data: { fileId: string }): void;
  fetchAudioSource(data: { fileId: string }): void;
  fetchAudioTranscripts(data: { fileId: string; dataVersion: string }): void;
}

const useFilesHook = (
  dossierId: string
): [FilesHookState, FilesHookHandler] => {
  const dispatch = useDispatch();
  const {
    files,
    filesContent,
    filesOCR,
    filesNotepad,
    fileSource,
    audioTranscripts,
    actions,
  } = useSelector((s: StoreState) => s['files']);

  const handler = React.useMemo(
    () => ({
      fetchFiles: () => {
        dispatch(filesSliceActions.files(dossierId));
      },
      fetchFileContents: (data: {
        fileId: string;
        startPage: number;
        endPage: number;
      }) => {
        const { fileId, startPage, endPage } = data;
        dispatch(
          filesSliceActions.fetchFileContents({
            dossierId,
            fileId,
            startPage,
            endPage,
          })
        );
      },
      downloadFile: (fileId: string) => {
        dispatch(filesSliceActions.downloadFile(fileId));
      },
      deleteFiles: (fileIds: string[]) => {
        dispatch(filesSliceActions.deleteFiles({ dossierId, fileIds }));
      },
      fetchFilesOCR: (data: { fileId: string; dataVersion: string }) => {
        const { fileId, dataVersion } = data;
        dispatch(
          filesSliceActions.fetchFilesOCR({
            dossierId,
            fileId,
            dataVersion,
          })
        );
      },
      fetchFileNotepads: (data: { fileId: string; dataVersion: string }) => {
        const { fileId, dataVersion } = data;
        dispatch(
          filesSliceActions.fetchFileNotepads({
            dossierId,
            fileId,
            dataVersion,
          })
        );
      },
      fetchFileSource: (data: { fileId: string }) => {
        const { fileId } = data;
        dispatch(filesSliceActions.fetchFileSource({ dossierId, fileId }));
      },
      fetchAudioSource: (data: { fileId: string }) => {
        const { fileId } = data;
        dispatch(filesSliceActions.fetchAudioSource({ dossierId, fileId }));
      },
      fetchAudioTranscripts: (data: {
        fileId: string;
        dataVersion: string;
      }) => {
        const { fileId, dataVersion } = data;
        dispatch(
          filesSliceActions.fetchAudioTranscript({
            dossierId,
            fileId,
            dataVersion,
          })
        );
      },
    }),
    [dossierId]
  );

  return [
    {
      files: files[dossierId] || {},
      filesContent,
      filesOCR,
      filesNotepad,
      fileSource,
      audioTranscripts,
      actions,
    },
    handler,
  ];
};

export default useFilesHook;
